<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  reportId: { type: Number, default: null },
});

const { t } = useI18n();
const store = useStore();

const agentList = useMapGetter('agents/getAgents');
const teamList = useMapGetter('teams/getTeams');

const dialogRef = ref(null);
const selectedAgents = ref([]);
const selectedTeams = ref([]);
const isSaving = ref(false);

// IDs fetched from the server, waiting to be resolved into objects once the
// agent/team lists are available. Not written to by multiselect edits.
const pendingAgentIds = ref(null);
const pendingTeamIds = ref(null);

const hydrateSelections = () => {
  if (pendingAgentIds.value !== null && (agentList.value || []).length) {
    const ids = pendingAgentIds.value;
    selectedAgents.value = agentList.value.filter(a => ids.includes(a.id));
    pendingAgentIds.value = null;
  }
  if (pendingTeamIds.value !== null && (teamList.value || []).length) {
    const ids = pendingTeamIds.value;
    selectedTeams.value = teamList.value.filter(tm => ids.includes(tm.id));
    pendingTeamIds.value = null;
  }
};

const open = async () => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');

  selectedAgents.value = [];
  selectedTeams.value = [];
  pendingAgentIds.value = null;
  pendingTeamIds.value = null;

  dialogRef.value?.open();

  if (props.reportId) {
    const data = await store.dispatch(
      'dataAssistantReports/fetchReportCollaborators',
      props.reportId
    );
    // Team-based permissions also materialize member records on the backend.
    // Hide those derived members in the UI so the team stays the single
    // source of truth for this report.
    const teamIds = data?.team_ids || [];
    if (teamIds.length) {
      pendingAgentIds.value = [];
      pendingTeamIds.value = teamIds;
    } else {
      pendingAgentIds.value = data?.user_ids || [];
      pendingTeamIds.value = [];
    }
    hydrateSelections();
  }
};

const close = () => {
  dialogRef.value?.close();
};

defineExpose({ open, close });

// Hydrate once whichever side (lists vs. fetched ids) arrives last.
watch(agentList, hydrateSelections);
watch(teamList, hydrateSelections);

// Agents and teams are mutually exclusive: picking one clears the other.
watch(selectedTeams, newVal => {
  if (newVal.length && selectedAgents.value.length) selectedAgents.value = [];
});
watch(selectedAgents, newVal => {
  if (newVal.length && selectedTeams.value.length) selectedTeams.value = [];
});

const save = async () => {
  if (!props.reportId) return;
  isSaving.value = true;
  try {
    await store.dispatch('dataAssistantReports/updateReportCollaborators', {
      reportId: props.reportId,
      userIds: selectedAgents.value.map(a => a.id),
      teamIds: selectedTeams.value.map(tm => tm.id),
    });
    useAlert(t('DATA_ASSISTANT.COLLABORATORS.SAVED'));
    close();
  } catch (e) {
    useAlert(t('DATA_ASSISTANT.COLLABORATORS.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('DATA_ASSISTANT.COLLABORATORS.TITLE')"
    :description="t('DATA_ASSISTANT.COLLABORATORS.DESCRIPTION')"
    :confirm-button-label="t('DATA_ASSISTANT.COLLABORATORS.SAVE')"
    :is-loading="isSaving"
    width="xl"
    @confirm="save"
  >
    <div class="flex flex-col gap-4">
      <div>
        <label class="mb-2 block text-sm font-medium text-n-slate-12">
          {{ t('DATA_ASSISTANT.COLLABORATORS.AGENTS') }}
        </label>
        <multiselect
          v-model="selectedAgents"
          :options="agentList"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="t('DATA_ASSISTANT.COLLABORATORS.SELECT_AGENTS')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        />
      </div>

      <div>
        <label class="mb-2 block text-sm font-medium text-n-slate-12">
          {{ t('DATA_ASSISTANT.COLLABORATORS.TEAMS') }}
        </label>
        <multiselect
          v-model="selectedTeams"
          :options="teamList"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="t('DATA_ASSISTANT.COLLABORATORS.SELECT_TEAMS')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        />
      </div>
    </div>
  </Dialog>
</template>
